import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Loader2, Clock, BarChart3, Pencil, Save, X, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const ExamSubmissions = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedSubmission, setSelectedSubmission] = useState<string | null>(null);
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null);
  const [editScore, setEditScore] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editCorrect, setEditCorrect] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: exam, isLoading: examLoading } = useQuery({
    queryKey: ["exam-info", examId],
    queryFn: async () => {
      const { data } = await supabase.from("exams").select("*, classes(name)").eq("id", examId!).single();
      return data;
    },
    enabled: !!examId,
  });

  const { data: submissions, isLoading } = useQuery({
    queryKey: ["exam-submissions", examId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_submissions")
        .select("*, children(full_name)")
        .eq("exam_id", examId!)
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!examId,
  });

  const { data: answers } = useQuery({
    queryKey: ["submission-answers", selectedSubmission],
    queryFn: async () => {
      const { data } = await supabase
        .from("exam_answers")
        .select("*, questions(question_text, question_type, correct_answer, options, explanation)")
        .eq("submission_id", selectedSubmission!);
      return data || [];
    },
    enabled: !!selectedSubmission,
  });

  const startEdit = (a: any) => {
    setEditingAnswerId(a.id);
    setEditScore(a.score?.toString() || "0");
    setEditNotes(a.teacher_notes || "");
    setEditCorrect(a.is_correct);
  };

  const cancelEdit = () => {
    setEditingAnswerId(null);
  };

  const saveEdit = async (answerId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase.from("exam_answers").update({
        score: parseFloat(editScore) || 0,
        teacher_notes: editNotes || null,
        is_correct: editCorrect,
      }).eq("id", answerId);

      if (error) throw error;

      // Recalculate submission totals
      if (selectedSubmission) {
        const { data: allAnswers } = await supabase
          .from("exam_answers")
          .select("score, is_correct")
          .eq("submission_id", selectedSubmission);

        if (allAnswers) {
          const totalScore = allAnswers.reduce((sum, a) => sum + (a.score || 0), 0);
          const sub = submissions?.find((s: any) => s.id === selectedSubmission);
          const maxScore = sub?.max_score || totalScore;
          const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

          await supabase.from("exam_submissions").update({
            total_score: totalScore,
            percentage,
            status: "graded",
          }).eq("id", selectedSubmission);
        }
      }

      toast({ title: "تم حفظ التعديل بنجاح ✅" });
      setEditingAnswerId(null);
      queryClient.invalidateQueries({ queryKey: ["submission-answers", selectedSubmission] });
      queryClient.invalidateQueries({ queryKey: ["exam-submissions", examId] });
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (examLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="text-5xl animate-bounce">📊</div>
        <p className="text-muted-foreground">جاري تحميل النتائج...</p>
      </div>
    );
  }

  const avgScore = submissions && submissions.length > 0
    ? Math.round(submissions.reduce((sum: number, s: any) => sum + (s.percentage || 0), 0) / submissions.length) : 0;
  const passCount = submissions ? submissions.filter((s: any) => (s.percentage || 0) >= 70).length : 0;
  const highestScore = submissions && submissions.length > 0
    ? Math.max(...submissions.map((s: any) => s.percentage || 0)) : 0;

  const chartData = (submissions || []).map((s: any) => ({
    name: s.children?.full_name?.split(" ")[0] || "—",
    percentage: s.percentage || 0,
  }));

  const getBarColor = (pct: number) => pct >= 70 ? "#22c55e" : pct >= 50 ? "#eab308" : "#ef4444";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/lessons")} className="rounded-full">
          <ArrowLeft className="w-4 h-4 ml-1" />العودة
        </Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <span className="text-2xl">📊</span>
            {exam?.title || "نتائج الامتحان"}
          </h1>
          {(exam as any)?.classes?.name && <p className="text-sm text-muted-foreground">{(exam as any).classes.name}</p>}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: "👥", value: (submissions || []).length, label: "متقدمين", color: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/10" },
          { icon: "📈", value: `${avgScore}%`, label: "المتوسط", color: "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/10" },
          { icon: "✅", value: passCount, label: "ناجحون", color: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/10" },
          { icon: "🏆", value: `${highestScore}%`, label: "أعلى درجة", color: "from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-900/10" },
        ].map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <Card className={`bg-gradient-to-b ${stat.color} border-0`}>
              <CardContent className="p-4 text-center">
                <span className="text-2xl">{stat.icon}</span>
                <p className="text-2xl font-black text-foreground mt-1">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              توزيع الدرجات
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis domain={[0, 100]} fontSize={12} />
                <Tooltip formatter={(v: any) => [`${v}%`, "النسبة"]} />
                <Bar dataKey="percentage" radius={[6, 6, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={getBarColor(entry.percentage)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Submissions List */}
        <div className="space-y-3">
          <h3 className="font-bold flex items-center gap-2">📋 النتائج</h3>
          {(submissions || []).length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="py-12 text-center">
                <div className="text-4xl mb-2">📭</div>
                <p className="text-muted-foreground">لم يتقدم أحد بعد</p>
              </CardContent>
            </Card>
          ) : (
            (submissions || []).map((s: any, i: number) => (
              <motion.div key={s.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                <Card
                  className={`cursor-pointer transition-all border-2 ${
                    selectedSubmission === s.id ? "ring-2 ring-primary border-primary" : "hover:shadow-md hover:border-primary/30"
                  }`}
                  onClick={() => setSelectedSubmission(s.id)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                        (s.percentage || 0) >= 70 ? "bg-green-100" : (s.percentage || 0) >= 50 ? "bg-yellow-100" : "bg-red-100"
                      }`}>
                        {(s.percentage || 0) >= 70 ? "⭐" : (s.percentage || 0) >= 50 ? "👍" : "💪"}
                      </div>
                      <div>
                        <p className="font-bold text-sm">{s.children?.full_name || "طفل"}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {s.submitted_at ? format(new Date(s.submitted_at), "d MMM HH:mm", { locale: ar }) : "—"}
                        </p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className={`text-xl font-black ${(s.percentage || 0) >= 70 ? "text-green-600" : (s.percentage || 0) >= 50 ? "text-yellow-600" : "text-red-600"}`}>
                        {s.percentage ?? 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">{s.total_score}/{s.max_score}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </div>

        {/* Answer Details with Edit */}
        <div className="space-y-3">
          <h3 className="font-bold flex items-center gap-2">📝 تفاصيل الإجابات</h3>
          {!selectedSubmission ? (
            <Card className="border-2 border-dashed">
              <CardContent className="py-12 text-center">
                <div className="text-4xl mb-2">👈</div>
                <p className="text-muted-foreground">اختر طفل لعرض إجاباته</p>
              </CardContent>
            </Card>
          ) : !answers ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-2 pl-2">
                {answers.map((a: any, i: number) => {
                  const isEditing = editingAnswerId === a.id;
                  return (
                    <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                      <Card className={`border-2 ${a.is_correct === true ? "border-green-200 bg-green-50/30 dark:border-green-800 dark:bg-green-900/10" : a.is_correct === false ? "border-red-200 bg-red-50/30 dark:border-red-800 dark:bg-red-900/10" : ""}`}>
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-start justify-between">
                            <p className="text-sm font-medium">{i + 1}. {(a.questions as any)?.question_text}</p>
                            <div className="flex items-center gap-1 shrink-0">
                              {!isEditing ? (
                                <>
                                  <span className="text-lg">{a.is_correct === true ? "✅" : a.is_correct === false ? "❌" : "📝"}</span>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(a)}>
                                    <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={cancelEdit} disabled={saving}>
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={() => saveEdit(a.id)} disabled={saving}>
                                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>

                          <p className="text-xs">
                            <span className="text-muted-foreground">الإجابة: </span>
                            <span className={a.is_correct === false ? "text-red-600 font-medium" : "font-medium"}>{a.answer || "—"}</span>
                          </p>

                          {!isEditing && a.is_correct === false && (
                            <p className="text-xs">
                              <span className="text-muted-foreground">الصحيحة: </span>
                              <span className="text-green-600 font-bold">{(a.questions as any)?.correct_answer}</span>
                            </p>
                          )}

                          {!isEditing && a.teacher_notes && (
                            <p className="text-xs text-muted-foreground bg-muted/50 rounded p-1.5">🗒️ {a.teacher_notes}</p>
                          )}

                          {!isEditing && (a.questions as any)?.explanation && (
                            <p className="text-xs text-muted-foreground bg-muted/50 rounded p-1.5">💡 {(a.questions as any).explanation}</p>
                          )}

                          {!isEditing && a.score != null && (
                            <Badge variant="outline" className="text-xs">الدرجة: {a.score}</Badge>
                          )}

                          {/* Edit Mode */}
                          {isEditing && (
                            <div className="space-y-2 pt-1 border-t border-border">
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-muted-foreground whitespace-nowrap">صح/خطأ:</label>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant={editCorrect === true ? "default" : "outline"}
                                    className="h-7 px-2 text-xs"
                                    onClick={() => setEditCorrect(true)}
                                  >
                                    ✅ صح
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={editCorrect === false ? "destructive" : "outline"}
                                    className="h-7 px-2 text-xs"
                                    onClick={() => setEditCorrect(false)}
                                  >
                                    ❌ خطأ
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-muted-foreground whitespace-nowrap">الدرجة:</label>
                                <Input
                                  type="number"
                                  value={editScore}
                                  onChange={(e) => setEditScore(e.target.value)}
                                  className="h-7 w-20 text-xs"
                                  min={0}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground">ملاحظات المدرس:</label>
                                <Textarea
                                  value={editNotes}
                                  onChange={(e) => setEditNotes(e.target.value)}
                                  placeholder="أضف ملاحظة..."
                                  rows={2}
                                  className="text-xs mt-1"
                                />
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamSubmissions;
